import { useEffect, useRef, useState } from "react";
import { http, fmtIDR, fmtNum } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Landmark, Bell, ArrowDownCircle, ArrowUpCircle, ArrowUpDown, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const POLL_MS = 12000;
const LS_LAST_SEEN = "transfer_notif_last_seen_at";
const LS_ACKED = "transfer_notif_acked_ids"; // fallback if backend ack fails

function playChime() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const now = ctx.currentTime;
    const notes = [880, 1174, 1568];
    const dur = 0.14;
    notes.forEach((f, i) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.setValueAtTime(f, now + i * dur);
      g.gain.setValueAtTime(0.0001, now + i * dur);
      g.gain.exponentialRampToValueAtTime(0.28, now + i * dur + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + i * dur + dur - 0.02);
      osc.connect(g).connect(ctx.destination);
      osc.start(now + i * dur);
      osc.stop(now + i * dur + dur);
    });
    setTimeout(() => { try { ctx.close(); } catch {} }, 900);
  } catch {}
}

function DirectionIcon({ dir }) {
  if (dir === "in") return <ArrowDownCircle size={13} className="text-emerald-600" />;
  if (dir === "out") return <ArrowUpCircle size={13} className="text-rose-600" />;
  return <ArrowUpDown size={13} className="text-amber-600" />;
}

/**
 * Global Transfer Notification Bell — visible to ALL logged-in users (HQ +
 * every branch). Fires bell + toast + audio chime the moment a transfer or
 * mixed-payment transaction is saved anywhere in the network. Users must
 * acknowledge (m-banking cross-check) each entry before it drops off the
 * unread badge.
 */
export default function TransferNotificationBell({ onCount } = {}) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const knownIds = useRef(new Set());
  const bootstrapped = useRef(false);
  const [lastSeenAt, setLastSeenAt] = useState(() => localStorage.getItem(LS_LAST_SEEN) || "");

  const fetchList = async () => {
    try {
      const { data } = await http.get("/transfer-notifications?limit=30");
      const arr = Array.isArray(data) ? data : [];
      arr.sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
      // Detect new ones since last poll
      const newOnes = arr.filter((a) => !knownIds.current.has(a.id));
      arr.forEach((a) => knownIds.current.add(a.id));
      if (bootstrapped.current && newOnes.length > 0) {
        playChime();
        newOnes.slice(0, 3).forEach((a) => {
          const dir = a.direction === "in" ? "MASUK" : a.direction === "out" ? "KELUAR" : "MIXED";
          toast.warning(
            `Transfer ${dir} · ${a.branch_name} · ${a.customer_name} · Rp ${fmtNum(a.transfer_idr || a.total_idr, 0)}`,
            { description: "Cek m-banking rek. PT Emerald Valasindo untuk verifikasi.", duration: 8000 }
          );
        });
      }
      bootstrapped.current = true;
      setItems(arr);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!user) return;
    fetchList();
    const t = setInterval(fetchList, POLL_MS);
    const onRefresh = () => fetchList();
    window.addEventListener("emc:transfer-refresh", onRefresh);
    return () => { clearInterval(t); window.removeEventListener("emc:transfer-refresh", onRefresh); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const unread = items.filter((i) => !i.acknowledged && (!lastSeenAt || i.created_at > lastSeenAt)).length;
  useEffect(() => { onCount?.(unread); }, [unread]);

  const openDialog = () => {
    setOpen(true);
    const now = new Date().toISOString();
    localStorage.setItem(LS_LAST_SEEN, now);
    setLastSeenAt(now);
  };

  const acknowledge = async (n) => {
    try {
      await http.post(`/transfer-notifications/${n.id}/ack`);
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, acknowledged: true } : x)));
      toast.success("Notifikasi ditandai selesai dicek");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal ack notifikasi");
    }
  };

  if (!user) return null;

  return (
    <>
      <button
        type="button"
        onClick={openDialog}
        className="relative inline-flex items-center justify-center w-9 h-9 rounded-md border border-slate-200 bg-white/60 text-slate-600 hover:bg-slate-100 transition"
        data-testid="transfer-notif-bell"
        title="Notifikasi Transfer (cek m-banking Emerald)"
      >
        <Landmark size={18} className={unread > 0 ? "text-amber-600" : "text-slate-500"} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-amber-600 text-white text-[9px] font-bold flex items-center justify-center animate-pulse">
            {unread}
          </span>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell size={16} /> Notifikasi Transfer — Verifikasi M-Banking
            </DialogTitle>
          </DialogHeader>
          <div className="rounded-md border-l-4 border-amber-500 bg-amber-50 p-3 text-[12px] text-amber-900 leading-relaxed" data-testid="transfer-mbanking-warning">
            <strong>PENTING:</strong> Untuk setiap transaksi <em>Transfer</em>, mohon di cek di m-banking nasabah atau internet banking rekening <strong>PT Emerald Valasindo</strong>, berhasil keluar atau masuknya dana ke rekening perusahaan atau nasabah.
          </div>
          <div className="space-y-2 mt-3" data-testid="transfer-notif-list">
            {items.length === 0 && (
              <div className="text-sm text-slate-500 py-8 text-center">Belum ada notifikasi transfer.</div>
            )}
            {items.map((n) => (
              <div key={n.id} className={`border rounded-md p-3 transition ${n.acknowledged ? "bg-slate-50 border-slate-200" : "border-amber-300 bg-amber-50/40"}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
                      <DirectionIcon dir={n.direction} />
                      <span>{n.direction_label}</span>
                      <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-slate-800 text-white ml-1">{n.payment_method}</span>
                      {n.acknowledged && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 ml-1"
                          data-testid={`badge-checked-${n.id}`}
                          title={n.last_ack_by ? `Dicek oleh ${n.last_ack_by}${n.last_ack_at ? " · " + new Date(n.last_ack_at).toLocaleString("id-ID", { timeZone: "Asia/Jakarta" }) : ""}` : "Sudah diverifikasi"}>
                          <ShieldCheck size={10} /> Sudah Dicek
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {n.branch_name} · {n.customer_name} · Kasir: {n.cashier_name}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {new Date(n.created_at).toLocaleString("id-ID", { timeZone: "Asia/Jakarta" })} · TRX {(n.tx_id || "").slice(-8).toUpperCase()}
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {(n.items || []).map((it, i) => (
                        <span key={i} className={`px-1.5 py-0.5 rounded text-[10px] font-mono-num ${it.type === "jual" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>
                          {(it.type || "").toUpperCase()} {fmtNum(it.amount, 2)} {it.code}
                        </span>
                      ))}
                    </div>
                    {n.bank_account_label && (
                      <div className="text-[10px] text-slate-500 font-mono-num mt-1">→ {n.bank_account_label}</div>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-bold text-slate-900 font-mono-num">{fmtIDR(n.transfer_idr || n.total_idr)}</div>
                    {!n.acknowledged ? (
                      <Button size="sm" onClick={() => acknowledge(n)} className="mt-1.5 h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700" data-testid={`btn-ack-${n.id}`}>
                        <ShieldCheck size={11} className="mr-1" /> Sudah dicek
                      </Button>
                    ) : (
                      <div className="mt-1.5 text-[10px] text-emerald-700 font-semibold flex items-center gap-1 justify-end">
                        <ShieldCheck size={11} /> Terverifikasi
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
